/*
Blog Rich Text is for rich text renders in the blog specifically.
Blogs support a lot of elements, but also a lot of embedded assets and entries.
This is not suggested for use outside of the blog as it is not generic enough.

Its caller is `components/Blog/Blog.tsx`, which renders a Metal Market Intelligence article's
`body` (`src/contentful/blog/types` → `AllContentfulBlogItem`). The page
`pages/metal-resources/metal-market-intelligence/[slug].tsx` fetches that body at build time with
`Crawl().get("blog", { resolveRichText: true, ... })`, which replaces each embedded link's
`data.target` with the linked entry's or asset's fields and sets `entryType` on embedded entries.
The `entryType` cases below are Contentful content-type ids, so they must match the content model
exactly, including the spelling `blogThinklink`.

Node types handled:
- document, paragraph (white-space: pre-line, so soft line breaks show), heading-2 to heading-4,
  unordered-list, ordered-list, list-item, blockquote;
- table, table-row, table-cell, table-header-cell (a horizontally scrolling, striped table);
- hyperlink (always opens in a new tab);
- text with the bold, italic and underline marks (code and unknown marks render as plain text);
- embedded-entry-inline by entryType: blogVideo, blogButton, blogInfogram, blogThinklink;
  blogQuote and any other type render nothing;
- embedded-asset-block: an image, captioned with the asset description when it has one.

Every other node type renders an empty fragment and drops its content: heading-1, heading-5,
heading-6, hr, embedded-entry-block, and entry-hyperlink and asset-hyperlink, whose link text is
lost with them.

`components/Shared/model/Contentful.tsx` → `renderBlogRichText` is a second blog renderer that
works from the `raw` JSON string; it has no importer.
*/
import React, { ReactNode, Key } from "react";
import { Typography } from "@ryerson/frontend.typography";
import { Link } from "@ryerson/frontend.navigation";
import { Icon } from "@ryerson/frontend.assets";
import { Modal } from "@ryerson/frontend.modal";
import { Media, navigate } from "@ryerson/frontend.next";
import { Video } from "@ryerson/frontend.media";
import { Button } from "@ryerson/frontend.button";
import { NextRouter } from "next/router";
import styled from "@emotion/styled";
import { css } from "@emotion/react";
import { RichTextNode, Bold, Italic, Underline, Text } from "./";
import { ITheme, useTheme } from "@ryerson/frontend.theme";

const ImageContainer = styled.div`
	display: block;
	width: auto;
	height: auto;
	position: relative;
`;

const IconContainer = styled.div`
	display: block;
	position: absolute;
	top: 7px;
	text-align: center;
	line-height: 28px;
	border-radius: 24px;
	height: 24px;
	width: 24px;
	right: 7px;
`;

/**
 * Mobile image frame: overlays a "scan" icon on the image, and clicking it opens the same image in
 * a closable modal. Used below the `lg` breakpoint only.
 */
const ImageWrapper = (props: any) => {
	const { theme } = useTheme();
	const [modalOpen, setModalOpen] = React.useState<boolean>(false);
	return (
		<>
			<ImageContainer>
				<IconContainer
					css={css`
						background-color: rgba(255, 255, 255, 0.8);
					`}
				>
					<Icon
						icon="scan"
						size="xs"
						color={theme.colors.primary.header}
						circled={false}
						selected={false}
						onClick={() => {
							setModalOpen(true);
						}}
					/>
				</IconContainer>
				{props.children ?? ""}
				<Modal
					isOpen={modalOpen}
					closable={true}
					onClose={() => {
						setModalOpen(false);
					}}
				>
					{props.children ?? ""}
				</Modal>
			</ImageContainer>
		</>
	);
};

type ImageWithCaption = {
	image: string;
	title?: string;
	caption: string;
	type?: "primary" | "secondary" | "tertiary";
};

const ImageCaptionContainer = styled.div`
	display: block;
	width: calc(100% - 80px);
	height: auto;
	margin-top: 56px;
	margin-bottom: 56px;
	margin-left: 40px;
	${(props: any) => {
		const { theme } = props;
		return css`
			@media (max-width: ${theme.breakpoints.lg}) {
				width: calc(100% - 40px);
				margin-left: 20px;
				margin-top: 42px;
				margin-bottom: 42px;
			}
		`;
	}}
`;

const CaptionedImageContainer = styled.div`
	display: inline-block;
	width: calc(60% - 40px);
	vertical-align: bottom;
	height: auto;
	img {
		width: 100%;
		height: auto;
		display: block;
	}
	${(props: any) => {
		const { theme } = props;
		return css`
			@media (max-width: ${theme.breakpoints.lg}) {
				display: block;
				width: 100%;
				margin-bottom: 10px;
			}
		`;
	}}
`;
const CaptionContainer = styled.div`
	display: inline-block;
	width: 40%;
	margin-left: 40px;
	vertical-align: bottom;
	height: auto;
	${(props: any) => {
		const { theme } = props;
		return css`
			@media (max-width: ${theme.breakpoints.lg}) {
				display: block;
				width: 100%;
				margin-left: 0px;
			}
		`;
	}}
`;

/**
 * An embedded image with its caption beside it on desktop (60/40 split) and below it under the `lg`
 * breakpoint, where the image also gets the {@link ImageWrapper} zoom. `caption` is the asset's
 * Contentful description; `title` becomes the alt text.
 */
const CaptionedImage: React.FC<ImageWithCaption> = ({
	image,
	title,
	caption,
	type = "primary",
}) => {
	const { theme } = useTheme();
	return (
		<ImageCaptionContainer theme={theme}>
			<CaptionedImageContainer theme={theme}>
				<Media greaterThanOrEqual="lg">
					<img src={image} alt={title ?? "Image"} />
				</Media>
				<Media lessThan="lg">
					<ImageWrapper>
						<img src={image} alt={title ?? "Image"} />
					</ImageWrapper>
				</Media>
			</CaptionedImageContainer>
			<CaptionContainer theme={theme}>
				<Typography variant="div" type={type} size="sm">
					{caption}
				</Typography>
			</CaptionContainer>
		</ImageCaptionContainer>
	);
};

const VideoContainer = styled.div`
	width: 100%;
	display: block;
	height: 360px;
`;

const ThingLinkContainer = styled.div`
	display: block;
	width: 100%;
	height: auto;
`;

type Infogram = {
	infogramId: string;
	title: string;
};

/**
 * Embeds an Infogram chart for a `blogInfogram` entry. On mount it loads Infogram's embed loader
 * from `e.infogram.com` once per page (script id `infogram-async`), or, when the loader is already
 * initialised, asks it to process the new placeholder. The loader fills the `.infogram-embed` div
 * by its `data-id`.
 *
 * The script loads without any consent check. Infogram counts as a third-party tag under the site
 * rule that no analytics or ad tag fires before consent, so loading it here breaks that rule: a
 * visitor who has not accepted cookies and opens an article with a chart still gets the
 * `e.infogram.com` script.
 */
const Infogrammer = (props: Infogram) => {
	React.useEffect(() => {
		let t: any = "InfogramEmbeds";
		let d = document.getElementsByTagName("script")[0];
		if ((window as any)[t] && (window as any)[t].initialized)
			(window as any)[t].process && (window as any)[t].process();
		else if (!document.getElementById("infogram-async")) {
			var o = document.createElement("script");
			o.async = true;
			o.id = "infogram-async";
			o.src = "https://e.infogram.com/js/dist/embed-loader-min.js";
			if (d.parentNode) d.parentNode.insertBefore(o, d);
		}
	}, []);
	return (
		<ThingLinkContainer>
			<div
				className="infogram-embed"
				data-id={props.infogramId}
				data-type="interactive"
				data-title={props.title}
			></div>
		</ThingLinkContainer>
	);
};

type ThingLink = {
	thinglinkId: string;
	originalHeight: number;
	originalWidth: number;
};

/**
 * Embeds a ThingLink card (`thinglink.com/card/<id>`) for a `blogThinklink` entry in an iframe
 * sized to the container's width, keeping the aspect ratio of `originalWidth` × `originalHeight`.
 * It resizes on window resize; the resize listener is never removed.
 */
const ThingLinker = (props: ThingLink) => {
	const isWindow = typeof window !== "undefined";
	const thinglinkId = props.thinglinkId;
	const aspect = props.originalHeight / props.originalWidth;
	const [thingH, setThingH] = React.useState<number>(props.originalHeight);
	const [thingW, setThingW] = React.useState<number>(props.originalWidth);
	React.useEffect(() => {
		if (isWindow) {
			if (document && document.getElementById(thinglinkId) !== null) {
				let width = document.getElementById(thinglinkId)?.offsetWidth;
				if (width) {
					setThingW(width);
					let height = width * aspect;
					setThingH(height);
				}
			}
			window.addEventListener("resize", () => {
				if (document && document.getElementById(thinglinkId) !== null) {
					let width = document.getElementById(thinglinkId)?.offsetWidth;
					if (width) {
						setThingW(width);
						let height = width * aspect;
						setThingH(height);
					}
				}
			});
		}
	}, []);
	return (
		<ThingLinkContainer id={props.thinglinkId}>
			<iframe
				width={thingW}
				height={thingH}
				data-original-width={props.originalWidth}
				data-original-height={props.originalHeight}
				css={css`
					border: none;
				`}
				scrolling="no"
				src={"https://www.thinglink.com/card/" + props.thinglinkId}
			></iframe>
		</ThingLinkContainer>
	);
};

/**
 * Renders one node of a resolved blog-article rich-text tree (start with the `document` node) and,
 * recursively, its children. The node types it draws are listed in this file's header.
 *
 * `type` is the design-system colour context for text and links; `theme` supplies table colours,
 * breakpoints and the heading-4 font; `key` is the child index the parent supplies. `router` is
 * only passed down the recursion and isn't read.
 *
 * Returns `null` for a missing node or one without a string `nodeType`, and `undefined` for an
 * `embedded-entry-inline` node without an `entryType` (an embed `Crawl` couldn't resolve). The
 * embed cases read `node.data.target` fields without checks, so an embedded entry missing a
 * required field (for example a `blogVideo` without a thumbnail) throws. Page content renders in
 * the browser only (`components/NextLayouts/RootLayout.tsx` → `HasMounted`), so the throw happens
 * on the visitor's page, not in the build.
 */
export const renderBlogText = (
	node: RichTextNode,
	type: "primary" | "secondary" | "tertiary" = "primary",
	router: NextRouter,
	theme: ITheme,
	key?: Key
): React.ReactNode => {
	if (!node || typeof node.nodeType !== "string") return null;

	switch (node.nodeType) {
		case "text": {
			const value = node.value ?? "";
			if (!node.marks?.length) return value;

			return node.marks.reduce<ReactNode>((acc, mark) => {
				switch (mark.type) {
					case "bold":
						return <Bold>{acc}</Bold>;
					case "italic":
						return <Italic>{acc}</Italic>;
					case "underline":
						return <Underline>{acc}</Underline>;
					case "code": //unsupported
						return <Text>{acc}</Text>;
					default:
						return <Text>{acc}</Text>;
				}
			}, value);
		}
		case "paragraph":
			return (
				<Typography
					key={key}
					variant="p"
					css={css`
						white-space: pre-line;
					`}
					type={type}
				>
					{node.content?.map((n, i) => renderBlogText(n, type, router, theme, i))}
				</Typography>
			);
		case "heading-2":
			return (
				<Typography
					key={key}
					css={css`
						font-size: 20px;
						line-height: 25px;
						letter-spacing: -1px;
					`}
					variant="h2"
					type={type}
				>
					{node.content?.map((n, i) => renderBlogText(n, type, router, theme, i))}
				</Typography>
			);
		case "heading-3":
			return (
				<Typography
					key={key}
					css={css`
						font-size: 16px;
						line-height: 25px;
						letter-spacing: -1px;
					`}
					variant="h3"
					type={type}
				>
					{node.content?.map((n, i) => renderBlogText(n, type, router, theme, i))}
				</Typography>
			);
		case "heading-4":
			return (
				<Typography
					key={key}
					variant="h4"
					css={css`
						font-size: 16px;
						font-weight: 500;
						font-family: ${theme.typography.fontFamilies.primary};
						line-height: 20px;
						@media (max-width: ${theme.breakpoints.lg}) {
							font-size: 14px;
						}
					`}
					type={type}
				>
					{node.content?.map((n, i) => renderBlogText(n, type, router, theme, i))}
				</Typography>
			);
		case "unordered-list":
			return (
				<ul key={key}>
					{node.content?.map((n, i) => renderBlogText(n, type, router, theme, i))}
				</ul>
			);
		case "ordered-list":
			return (
				<ol key={key}>
					{node.content?.map((n, i) => renderBlogText(n, type, router, theme, i))}
				</ol>
			);
		case "list-item":
			return (
				<li key={key}>
					{node.content?.map((n, i) => renderBlogText(n, type, router, theme, i))}
				</li>
			);

		case "blockquote":
			return (
				<blockquote key={key}>
					{node.content?.map((n, i) => renderBlogText(n, type, router, theme, i))}
				</blockquote>
			);

		case "table":
			return (
				<div
					key={key}
					css={css`
						max-width: calc(100% - 80px);
						height: auto;
						display: block;
						overflow-x: auto;
						overflow-y: hidden;
						margin-left: 40px;
						border-radius: 3px;
						border-top: 3px solid ${theme.colors.tertiary.background};
						border-bottom: 3px solid ${theme.colors.tertiary.background};
						@media (max-width: ${theme.breakpoints.lg}) {
							margin-left: 20px;
							max-width: calc(100% - 40px);
						}
					`}
				>
					<table
						css={css`
							border-collapse: collapse;
							box-sizing: border-box;
							min-width: 100%;
							tr:nth-of-type(odd) {
								background-color: ${theme.colors.primary.background};
							}
							tr:nth-of-type(even) {
								background-color: ${theme.colors.secondary.background};
							}
						`}
					>
						<tbody>
							{node.content?.map((n, i) => renderBlogText(n, type, router, theme, i))}
						</tbody>
					</table>
				</div>
			);
		case "table-row":
			return (
				<tr key={key}>
					{node.content?.map((n, i) => renderBlogText(n, type, router, theme, i))}
				</tr>
			);
		case "table-cell":
			return (
				<td
					key={key}
					css={css`
						box-sizing: border-box;
						padding: 5px;
						border-bottom: 1px solid ${theme.colors.primary.lighterGray};
					`}
				>
					<Typography
						variant="span"
						size="sm"
						type="primary"
						css={css`
							p {
								margin: 0px;
								color: ${theme.colors.primary.text} !important;
							}
						`}
					>
						{node.content?.map((n, i) => renderBlogText(n, type, router, theme, i))}
					</Typography>
				</td>
			);
		case "table-header-cell":
			return (
				<th
					key={key}
					css={css`
						background-color: ${theme.colors.tertiary.background};
						box-sizing: border-box;
						padding: 5px;
						border-bottom: 1px solid ${theme.colors.tertiary.background};
						text-align: left;
					`}
				>
					<Typography
						variant="span"
						size="sm"
						css={css`
							p {
								margin: 0px;
								color: ${theme.colors.tertiary.header} !important;
							}
						`}
					>
						{node.content?.map((n, i) => renderBlogText(n, type, router, theme, i))}
					</Typography>
				</th>
			);
		case "hyperlink": {
			return (
				<Link key={key} type={type} to={(node.data?.uri as string) ?? "#"} target="_blank">
					{node.content?.map((n, i) => renderBlogText(n, type, router, theme, i))}
				</Link>
			);
		}
		case "embedded-entry-inline":
			if (node.entryType && node.entryType.length > 0) {
				//this is a embedded entry, we treat it differently based on its type
				switch (node.entryType) {
					case "blogVideo":
						return (
							<React.Fragment key={key}>
								<Media greaterThanOrEqual="lg">
									<VideoContainer>
										<Video
											videoId={node.data.target.videoId}
											buttonOrientation="right"
											imageUrl={node.data.target.thumbnail.file.url}
											text={node.data.target.title}
										/>
									</VideoContainer>
								</Media>
								<Media lessThan="lg">
									<VideoContainer
										css={css`
											height: 240px;
											margin-top: 40px;
										`}
									>
										<Video
											videoId={node.data.target.videoId}
											buttonOrientation="top-right"
											imageUrl={node.data.target.thumbnail.file.url}
											text={node.data.target.title}
										/>
									</VideoContainer>
								</Media>
							</React.Fragment>
						);
					// The entry's `themeColor` (lower-cased) is used as the Button `type`, and its
					// `url` opens as an external navigation.
					case "blogButton":
						return (
							<Button
								key={key}
								onClick={() => {
									navigate(node.data.target.url, {
										state: undefined,
										useExternal: true,
									});
								}}
								type={node.data.target.themeColor.toLowerCase()}
								size="md"
								shape="rounded"
								leftIcon="chevron-right"
								paddingType="strict"
								label={node.data.target.label}
							/>
						);
					case "blogInfogram":
						return (
							<Infogrammer
								key={key}
								infogramId={node.data.target.infogramId}
								title={node.data.target.title}
							/>
						);
					case "blogThinklink":
						return (
							<ThingLinker
								key={key}
								thinglinkId={node.data.target.thinglinkId}
								originalHeight={node.data.target.originalHeight}
								originalWidth={node.data.target.originalWidth}
							/>
						);
					case "blogQuote":
						return <></>;
					default:
						return <></>;
				}
			}
			// No entryType: the embedded link didn't resolve, and the function returns undefined.
			break;
		case "embedded-asset-block":
			return (
				<>
					<Media greaterThanOrEqual="lg">
						{node.data.target.description && node.data.target.description.length > 0 ? (
							<CaptionedImage
								title={node.data.target.title}
								caption={node.data.target.description}
								type={type ?? "primary"}
								image={node.data.target.file.url}
							/>
						) : (
							<img
								css={css`
									display: block;
									margin-left: 40px;
									margin-top: 56px;
									margin-bottom: 56px;
									max-width: calc(100% - 80px);
								`}
								src={node.data.target.file.url}
								alt={node.data.target.title}
							/>
						)}
					</Media>
					<Media lessThan="lg">
						{node.data.target.description && node.data.target.description.length > 0 ? (
							<CaptionedImage
								title={node.data.target.title}
								caption={node.data.target.description}
								type={type ?? "primary"}
								image={node.data.target.file.url}
							/>
						) : (
							<ImageWrapper>
								<img
									src={node.data.target.file.url}
									css={css`
										max-width: calc(100% - 40px);
										margin-left: 20px;
										margin-top: 34px;
										margin-bottom: 34px;
										display: block;
									`}
									alt={node.data.target.title}
								/>
							</ImageWrapper>
						)}
					</Media>
				</>
			);
		// Entry point of all rich text from contentful
		case "document":
			return <>{node.content?.map((n, i) => renderBlogText(n, type, router, theme, i))}</>;

		default:
			return <></>;
	}
};
